/**
 * Timeline Line Segment Component
 * Renders a vertical line segment connecting timeline dots
 *
 * DESIGN SPECS:
 * - 2px width
 * - Green color: secondary.main (matching brand/Figma design)
 * - Two variants: "solid" (continuous) or "dotted" (CSS gradient pattern)
 * - Absolute positioning within timeline column
 * - z-index: 1 (below dots, which are z-index: 2)
 *
 * VARIANT STYLES:
 * - "solid": Continuous line using backgroundColor
 * - "dotted": Repeating gradient pattern (4px dash, 4px gap)
 *   Uses: linear-gradient(theme.palette.primary.main 50%, transparent 50%)
 *   backgroundSize: "2px 8px"
 *
 * POSITIONING:
 * - Positioned absolutely within timeline column
 * - top: ${startY}px (where segment starts)
 * - height: ${segmentHeight}px (how tall the segment is)
 *
 * USAGE:
 * <TimelineLineSegment
 *   variant="dotted"
 *   startY={0}
 *   segmentHeight={100}
 *   testIdPrefix="pdp"
 * />
 */

import React from "react";
import { Box } from "@mui/material";
import { TIMELINE_LINE_WIDTH } from "./timeline-constants";

export interface TimelineLineSegmentProps {
  variant: "solid" | "dotted";
  startY: number; // Top position in pixels
  segmentHeight: number; // Height of segment in pixels
  testIdPrefix: string;
}

const TimelineLineSegment: React.FC<TimelineLineSegmentProps> = ({
  variant,
  startY,
  segmentHeight,
  testIdPrefix
}) => (
    <Box
      data-testid={`${testIdPrefix}_timeline-line-segment`}
      data-variant={variant}
      sx={{
        width: `${TIMELINE_LINE_WIDTH}px`,
        height: `${segmentHeight}px`,
        position: "absolute",
        top: `${startY}px`,
        left: "0",
        zIndex: 1,
        ...(variant === "solid"
          ? {
              backgroundColor: "secondary.main" // Green color from Figma design
            }
          : {
              backgroundColor: "transparent",
              backgroundImage: "linear-gradient(var(--theme-palette-secondary-main) 50%, transparent 50%)", // Green color
              backgroundSize: `${TIMELINE_LINE_WIDTH}px 8px`,
              backgroundRepeat: "repeat-y"
            })
      }}
    />
  );

export default TimelineLineSegment;
